import React, { useState } from "react";

import { CROP_TYPE_FCPO, PRACTICE_SLUG, PracticeSlug } from "models";

import { Col, FilterSet, FilterSetProps, InputSearch, Option, Row, Select } from "components";

type OnFilterChange = (key: string) => void;
type OnSortChange = (sort: { sortBy: "name" | "size" | "number"; sortOrder: "asc" | "desc" } | undefined) => void;

export type SortOption = {
  sortBy: "name" | "size" | "number";
  sortOrder: "asc" | "desc";
};

type SearchAndFilterProps = {
  setFilter: OnFilterChange;
  setSearch: OnFilterChange;
  setPage: (page: number) => void;
  setSort?: OnSortChange;
  type: "field" | "practice";
  practiceSlug?: PracticeSlug | PracticeSlug[];
  uniqueCrops?: string[]; // Unique crop names for filtering
  availableCropTypes?: CROP_TYPE_FCPO[]; // Available crop types for filtering
  availableYears?: number[]; // Available years for filtering (extracted from data)
  uniqueFertilizers?: string[]; // Unique fertilizer names for filtering
  availableFertilizerTypes?: ("mineralFertilizers" | "organicFertilizers")[]; // Available fertilizer types for filtering
  availableFertilizerYears?: number[]; // Available fertilizer years for filtering (extracted from data)
  uniqueTillages?: string[]; // Unique tillage names for filtering
  uniqueSeedings?: string[]; // Unique seeding names for filtering
  availableTillageYears?: number[]; // Available tillage years for filtering (extracted from data)
  availableSeedingYears?: number[]; // Available seeding years for filtering (extracted from data)
};

// Year range configuration - can be made configurable via props or constants
const DEFAULT_YEAR_RANGE = { start: 2020, end: 2026 };

const SORT_OPTIONS = [
  { value: "", label: "Default order" },
  { value: "name-asc", label: "Field name (A-Z)" },
  { value: "name-desc", label: "Field name (Z-A)" },
  { value: "size-asc", label: "Size (smallest first)" },
  { value: "size-desc", label: "Size (largest first)" },
  { value: "number-asc", label: "Field number (lowest first)" },
  { value: "number-desc", label: "Field number (highest first)" },
];

export const SearchAndFilter = ({
  setFilter,
  setSearch,
  setPage,
  setSort,
  type = "field",
  practiceSlug,
  uniqueCrops = [],
  availableCropTypes = [],
  availableYears,
  uniqueFertilizers = [],
  availableFertilizerTypes = [],
  availableFertilizerYears,
  uniqueTillages = [],
  uniqueSeedings = [],
  availableTillageYears,
  availableSeedingYears,
}: SearchAndFilterProps) => {
  const handleFilterChange = (filter: string) => {
    setFilter(filter);
    setPage(1);
  };

  const handleSearchChange = (s: string) => {
    setSearch(s);
    setPage(1);
  };

  const handleSortChange = (value: string) => {
    if (!setSort) return;
    if (!value) {
      setSort(undefined);
      return;
    }
    const [sortBy, sortOrder] = value.split("-") as ["name" | "size" | "number", "asc" | "desc"];
    setSort({ sortBy, sortOrder });
    setPage(1);
  };

  // Determine which pending filter to show based on practice type
  const getPendingFilterForPractice = (): { key: string; title: string } | null => {
    if (type === "field") {
      return { key: "pendingVerification", title: "pending verification" };
    }

    // For practice lists, show practice-specific pending filter
    if (!practiceSlug) return null;

    const slugs = Array.isArray(practiceSlug) ? practiceSlug : [practiceSlug];

    // Check for crops
    if (slugs.some((s) => s === PRACTICE_SLUG.CROPS)) {
      return { key: "pendingCrops", title: "pending crops" };
    }
    // Check for fertilizers
    if (slugs.some((s) => s === PRACTICE_SLUG.MINERAL_FERTILIZERS || s === PRACTICE_SLUG.ORGANIC_FERTILIZERS)) {
      return { key: "pendingFertilizers", title: "pending fertilizers" };
    }
    // Check for tillage & seeding
    if (slugs.some((s) => s === PRACTICE_SLUG.TILLAGES || s === PRACTICE_SLUG.SEEDINGS)) {
      return { key: "pendingTillageSeeding", title: "pending tillage & seeding" };
    }

    return null;
  };

  const pendingFilter = getPendingFilterForPractice();

  // Common editing request filter structure. leafSuffix keeps the nested open/toReview/reviewed
  // keys distinct per practice level, so they don't collide with the field/crop generic leaf keys.
  const createEditingRequestFilters = (key: string, leafSuffix: "" | "Fertilizer" | "TillageSeeding" = ""): FilterSetProps["filters"] => ({
    [key]: {
      title: "has editing requests",
      filters: {
        [`open${leafSuffix}EditingRequests`]: {
          title: "open editing requests",
        },
        [`toReview${leafSuffix}EditingRequests`]: {
          title: "to review editing requests",
        },
        [`reviewed${leafSuffix}EditingRequests`]: {
          title: "reviewed editing requests",
        },
      },
    },
  });

  // Get editing request filter structure based on type
  const getEditingRequestFilters = (): FilterSetProps["filters"] | null => {
    if (type === "field") {
      return createEditingRequestFilters("hasFieldEditingRequests");
    }

    // For practice lists, check if it's crops
    if (!practiceSlug) return null;

    const slugs = Array.isArray(practiceSlug) ? practiceSlug : [practiceSlug];
    if (slugs.some((s) => s === PRACTICE_SLUG.CROPS)) {
      return createEditingRequestFilters("hasCropEditingRequests");
    }
    if (slugs.some((s) => s === PRACTICE_SLUG.MINERAL_FERTILIZERS || s === PRACTICE_SLUG.ORGANIC_FERTILIZERS)) {
      return createEditingRequestFilters("hasFertilizerEditingRequests", "Fertilizer");
    }
    if (slugs.some((s) => s === PRACTICE_SLUG.TILLAGES || s === PRACTICE_SLUG.SEEDINGS)) {
      return createEditingRequestFilters("hasTillageSeedingEditingRequests", "TillageSeeding");
    }

    return null;
  };

  const editingRequestFilters = getEditingRequestFilters();

  // Generate years - use availableYears if provided, otherwise use default range
  const years =
    availableYears && availableYears.length > 0
      ? [...availableYears].sort((a, b) => a - b)
      : Array.from({ length: DEFAULT_YEAR_RANGE.end - DEFAULT_YEAR_RANGE.start + 1 }, (_, index) => DEFAULT_YEAR_RANGE.start + index);

  // Generate fertilizer years - use availableFertilizerYears if provided, otherwise use default range
  const fertilizerYears =
    availableFertilizerYears && availableFertilizerYears.length > 0
      ? [...availableFertilizerYears].sort((a, b) => a - b)
      : Array.from({ length: DEFAULT_YEAR_RANGE.end - DEFAULT_YEAR_RANGE.start + 1 }, (_, index) => DEFAULT_YEAR_RANGE.start + index);

  // Create crop filter structure with nested year filters
  const getCropFilters = (): FilterSetProps["filters"] | null => {
    if (type !== "practice" || !practiceSlug) return null;

    const slugs = Array.isArray(practiceSlug) ? practiceSlug : [practiceSlug];
    if (!slugs.some((s) => s === PRACTICE_SLUG.CROPS)) return null;

    // Always show the crops filter, even if crops are still loading
    // Create nested structure: crops -> [crop names] -> years -> [2020-2026]
    const cropFilters: Record<string, FilterSetProps["filters"][string]> = {};

    uniqueCrops.forEach((cropName) => {
      const yearFilters: Record<string, { title: string }> = {};
      years.forEach((year) => {
        yearFilters[`year-${year}`] = {
          title: year.toString(),
        };
      });

      cropFilters[`crop-${cropName}`] = {
        title: cropName,
        filters: yearFilters,
      };
    });

    return {
      crops: {
        title: "crops",
        filters: cropFilters,
      },
    };
  };

  // Create crop type filter structure with nested year filters
  const getCropTypeFilters = (): FilterSetProps["filters"] | null => {
    if (type !== "practice" || !practiceSlug) return null;

    const slugs = Array.isArray(practiceSlug) ? practiceSlug : [practiceSlug];
    if (!slugs.some((s) => s === PRACTICE_SLUG.CROPS)) return null;

    if (availableCropTypes.length === 0) return null;

    // Create nested structure: cropType -> [cover, undersowing, main] -> years -> [2020-2026]
    const cropTypeFilters: Record<string, FilterSetProps["filters"][string]> = {};

    availableCropTypes.forEach((cropType) => {
      const yearFilters: Record<string, { title: string }> = {};
      years.forEach((year) => {
        yearFilters[`year-${year}`] = {
          title: year.toString(),
        };
      });

      // Convert enum to lowercase for display (e.g., "COVER" -> "cover")
      const cropTypeLabel = cropType.toLowerCase();
      cropTypeFilters[`cropType-${cropType}`] = {
        title: cropTypeLabel,
        filters: yearFilters,
      };
    });

    return {
      cropType: {
        title: "crop type",
        filters: cropTypeFilters,
      },
    };
  };

  // Create fertilizer filter structure with nested year filters
  const getFertilizerFilters = (): FilterSetProps["filters"] | null => {
    if (type !== "practice" || !practiceSlug) return null;

    const slugs = Array.isArray(practiceSlug) ? practiceSlug : [practiceSlug];
    if (!slugs.some((s) => s === PRACTICE_SLUG.MINERAL_FERTILIZERS || s === PRACTICE_SLUG.ORGANIC_FERTILIZERS)) return null;

    // Always show the fertilizers filter, even if fertilizers are still loading
    // Create nested structure: fertilizers -> [fertilizer names] -> years -> [2020-2026]
    const fertilizerFilters: Record<string, FilterSetProps["filters"][string]> = {};

    uniqueFertilizers.forEach((fertilizerName) => {
      const yearFilters: Record<string, { title: string }> = {};
      fertilizerYears.forEach((year) => {
        yearFilters[`year-${year}`] = {
          title: year.toString(),
        };
      });

      fertilizerFilters[`fertilizer-${fertilizerName}`] = {
        title: fertilizerName,
        filters: yearFilters,
      };
    });

    return {
      fertilizers: {
        title: "fertilizers",
        filters: fertilizerFilters,
      },
    };
  };

  // Create fertilizer type filter structure with nested year filters
  const getFertilizerTypeFilters = (): FilterSetProps["filters"] | null => {
    if (type !== "practice" || !practiceSlug) return null;

    const slugs = Array.isArray(practiceSlug) ? practiceSlug : [practiceSlug];
    if (!slugs.some((s) => s === PRACTICE_SLUG.MINERAL_FERTILIZERS || s === PRACTICE_SLUG.ORGANIC_FERTILIZERS)) return null;

    if (availableFertilizerTypes.length === 0) return null;

    // Create nested structure: fertilizerType -> [mineral, organic] -> years -> [2020-2026]
    const fertilizerTypeFilters: Record<string, FilterSetProps["filters"][string]> = {};

    availableFertilizerTypes.forEach((fertilizerType) => {
      const yearFilters: Record<string, { title: string }> = {};
      fertilizerYears.forEach((year) => {
        yearFilters[`year-${year}`] = {
          title: year.toString(),
        };
      });

      // Convert to display label (e.g., "mineralFertilizers" -> "mineral")
      const fertilizerTypeLabel = fertilizerType === "mineralFertilizers" ? "mineral" : "organic";
      fertilizerTypeFilters[`fertilizerType-${fertilizerType}`] = {
        title: fertilizerTypeLabel,
        filters: yearFilters,
      };
    });

    return {
      fertilizerType: {
        title: "fertilizer type",
        filters: fertilizerTypeFilters,
      },
    };
  };

  // Helper function to create nested filter structure for tillage/seeding practices
  const createPracticeFiltersWithYears = (
    uniqueNames: string[],
    availableYears: number[] | undefined,
    prefix: string,
    title: string
  ): FilterSetProps["filters"] | null => {
    if (type !== "practice" || !practiceSlug) return null;

    const slugs = Array.isArray(practiceSlug) ? practiceSlug : [practiceSlug];
    if (!slugs.some((s) => s === PRACTICE_SLUG.TILLAGES || s === PRACTICE_SLUG.SEEDINGS)) return null;

    // Use available years if provided, otherwise use default range
    const years =
      availableYears && availableYears.length > 0
        ? [...availableYears].sort((a, b) => a - b)
        : Array.from({ length: DEFAULT_YEAR_RANGE.end - DEFAULT_YEAR_RANGE.start + 1 }, (_, index) => DEFAULT_YEAR_RANGE.start + index);

    // Create nested structure: [practice type] -> [practice names] -> years -> [2020-2026]
    const practiceFilters: Record<string, FilterSetProps["filters"][string]> = {};

    uniqueNames.forEach((practiceName) => {
      const yearFilters: Record<string, { title: string }> = {};
      years.forEach((year) => {
        yearFilters[`year-${year}`] = {
          title: year.toString(),
        };
      });

      practiceFilters[`${prefix}-${practiceName}`] = {
        title: practiceName,
        filters: yearFilters,
      };
    });

    return {
      [title]: {
        title,
        filters: practiceFilters,
      },
    };
  };

  // Create tillage filter structure with nested year filters
  const getTillageFilters = (): FilterSetProps["filters"] | null =>
    createPracticeFiltersWithYears(uniqueTillages, availableTillageYears, "tillage", "tillages");

  // Create seeding filter structure with nested year filters
  const getSeedingFilters = (): FilterSetProps["filters"] | null =>
    createPracticeFiltersWithYears(uniqueSeedings, availableSeedingYears, "seeding", "seedings");

  const cropFilters = getCropFilters();
  const cropTypeFilters = getCropTypeFilters();
  const fertilizerFilters = getFertilizerFilters();
  const fertilizerTypeFilters = getFertilizerTypeFilters();
  const tillageFilters = getTillageFilters();
  const seedingFilters = getSeedingFilters();

  return (
    <>
      <Row gutter={[16, 16]} className="margin-bottom-100">
        <Col md={8} sm={24}>
          <InputSearch label={"search"} onChange={(e) => handleSearchChange(e.target.value)} />
        </Col>
        {setSort && (
          <Col md={6} sm={24}>
            <Select label="Sort by" defaultValue="" onChange={handleSortChange} style={{ width: "100%" }}>
              {SORT_OPTIONS.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Col>
        )}
      </Row>

      <Row className="padding-bottom-200">
        <Col>
          <Filters
            onFilterChange={handleFilterChange}
            filters={
              {
                ...(pendingFilter && {
                  [pendingFilter.key]: {
                    title: pendingFilter.title,
                  },
                }),
                ...(editingRequestFilters || {}),
                ...(cropFilters || {}),
                ...(cropTypeFilters || {}),
                ...(fertilizerFilters || {}),
                ...(fertilizerTypeFilters || {}),
                ...(tillageFilters || {}),
                ...(seedingFilters || {}),
                allFields: {
                  title: "all fields",
                },
              } as FilterSetProps["filters"]
            }
          />
        </Col>
      </Row>
    </>
  );
};

const Filters = ({ onFilterChange, filters }: { onFilterChange: OnFilterChange; filters: FilterSetProps["filters"] }) => {
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);

  const handleFilterChange = (newFilters: string[]) => {
    setSelectedFilters(newFilters);
    // For nested filters (crop/year/cropType combinations), combine them; otherwise use the last filter key
    // If no filters selected, pass empty string to clear
    if (newFilters && newFilters.length > 0) {
      // Check if only parent filters are selected (fertilizers, crops, cropType, fertilizerType, tillages, seedings) - these should show all fields
      const lastFilter = newFilters[newFilters.length - 1];
      if (
        lastFilter === "fertilizers" ||
        lastFilter === "crops" ||
        lastFilter === "cropType" ||
        lastFilter === "fertilizerType" ||
        lastFilter === "tillages" ||
        lastFilter === "seedings"
      ) {
        // Parent filter selected - show all fields
        onFilterChange("");
        return;
      }

      // Check for filter combinations
      const cropIndex = newFilters.findIndex((f) => f.startsWith("crop-") && !f.startsWith("cropType-"));
      const cropTypeIndex = newFilters.findIndex((f) => f.startsWith("cropType-"));
      const fertilizerIndex = newFilters.findIndex((f) => f.startsWith("fertilizer-") && !f.startsWith("fertilizerType-"));
      const fertilizerTypeIndex = newFilters.findIndex((f) => f.startsWith("fertilizerType-"));
      const tillageIndex = newFilters.findIndex((f) => f.startsWith("tillage-"));
      const seedingIndex = newFilters.findIndex((f) => f.startsWith("seeding-"));
      const yearIndex = newFilters.findIndex((f) => f.startsWith("year-"));

      // Build filter parts array with consistent ordering
      const filterParts: string[] = [];
      if (cropIndex !== -1) filterParts.push(newFilters[cropIndex]);
      if (cropTypeIndex !== -1) filterParts.push(newFilters[cropTypeIndex]);
      if (fertilizerIndex !== -1) filterParts.push(newFilters[fertilizerIndex]);
      if (fertilizerTypeIndex !== -1) filterParts.push(newFilters[fertilizerTypeIndex]);
      if (tillageIndex !== -1) filterParts.push(newFilters[tillageIndex]);
      if (seedingIndex !== -1) filterParts.push(newFilters[seedingIndex]);
      if (yearIndex !== -1) filterParts.push(newFilters[yearIndex]);

      // Sort filter parts for consistent ordering: crop -> cropType -> fertilizer -> fertilizerType -> tillage -> seeding -> year
      const filterOrder: Record<string, number> = {
        "crop-": 0,
        "cropType-": 1,
        "fertilizer-": 2,
        "fertilizerType-": 3,
        "tillage-": 4,
        "seeding-": 5,
        "year-": 6,
      };
      filterParts.sort((a, b) => {
        const aPrefix = Object.keys(filterOrder).find((k) => a.startsWith(k)) || "";
        const bPrefix = Object.keys(filterOrder).find((k) => b.startsWith(k)) || "";
        return (filterOrder[aPrefix] ?? 99) - (filterOrder[bPrefix] ?? 99);
      });

      if (filterParts.length > 1) {
        // Combine multiple filters: e.g., "crop-Kartoffeln|year-2026" or "cropType-cover|year-2026"
        onFilterChange(filterParts.join("|"));
      } else if (filterParts.length === 1) {
        // Single filter
        onFilterChange(filterParts[0]);
      } else {
        // Use the last (leaf) filter key if no recognized filters found
        onFilterChange(newFilters[newFilters.length - 1]);
      }
    } else {
      onFilterChange("");
    }
  };

  const handleUnselectFilter = (index: number) => {
    // Remove the filter at the given index and all filters after it
    const newFilters = selectedFilters.slice(0, index);
    handleFilterChange(newFilters);
  };

  return (
    <FilterSet
      onClearFilters={() => handleFilterChange([])}
      onSelectFilter={(key: string) => {
        // When selecting a filter, add it to the current path
        handleFilterChange([...selectedFilters, key]);
      }}
      onUnselectFilter={handleUnselectFilter}
      selectedFilters={selectedFilters}
      filters={filters}
    />
  );
};
